import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { DataKeys } from "../../types";
import { HealthProductCapabilityRow } from "./HealthProductCapabilityRow";
import { HealthProductSuiteIcon } from "./HealthProductSuiteIcons";
import {
    AimmaProductSuiteGroupId,
    getLocalized,
    HealthLang,
} from "./healthTypes";
import {
    healthProductGroupBridgeBody,
    healthProductGroupBridgeEyebrow,
    healthProductGroupCardBridge,
    healthProductGroupCardBridgeLinked,
    healthProductGroupCardDeveloping,
    healthProductGroupCardHint,
    healthProductGroupCardHintDoctor,
    healthProductGroupCardHintDoctorCell,
    healthProductGroupCardHintPinned,
    healthProductGroupCardLinked,
    healthProductGroupCardStatic,
    healthProductGroupHeader,
    healthProductGroupHeaderText,
    healthProductGroupProductList,
    healthProductGroupProductListDoctorGrid,
    healthProductGroupTitle,
    healthButtonArrowFaSm,
    healthLinkedCardInnerStable,
    healthProductGroupLinkedInner,
} from "./healthVisualStyles";

interface HealthProductGroupCardProps {
    group: DataKeys;
    lang: HealthLang;
    className?: string;
}

function localeHealthPath(path: string, lang: HealthLang): string {
    if (lang === "Zh" && path.startsWith("/")) return `/tw${path}`;
    if (lang === "Jp" && path.startsWith("/")) return `/jp${path}`;
    return path;
}

function ProductGroupCardHint({
    label,
    variant = "footer",
    pinned = false,
}: {
    label: string;
    variant?: "footer" | "doctor-grid";
    pinned?: boolean;
}) {
    if (variant === "doctor-grid") {
        return (
            <div className={healthProductGroupCardHintDoctorCell}>
                <p className={healthProductGroupCardHintDoctor}>
                    {label}
                    <FontAwesomeIcon
                        icon={faArrowRight}
                        className={healthButtonArrowFaSm}
                        aria-hidden="true"
                    />
                </p>
            </div>
        );
    }

    return (
        <p className={pinned ? healthProductGroupCardHintPinned : healthProductGroupCardHint}>
            {label}
            <FontAwesomeIcon
                icon={faArrowRight}
                className={healthButtonArrowFaSm}
                aria-hidden="true"
            />
        </p>
    );
}

function ProductGroupCardBody({
    groupId,
    title,
    products,
    lang,
    isNursingRobot,
    isDoctor,
    isEducator,
    bridgeLabel,
    groupBody,
    hintLabel,
    pinFooterHint = false,
}: {
    groupId: AimmaProductSuiteGroupId;
    title: string;
    products: DataKeys[];
    lang: HealthLang;
    isNursingRobot: boolean;
    isDoctor: boolean;
    isEducator: boolean;
    bridgeLabel?: string;
    groupBody?: string;
    hintLabel?: string;
    pinFooterHint?: boolean;
}) {
    const listClass = isDoctor
        ? healthProductGroupProductListDoctorGrid
        : healthProductGroupProductList;

    const bodyContent = (
        <>
            <header className={healthProductGroupHeader}>
                <HealthProductSuiteIcon
                    size="group"
                    groupId={groupId}
                    muted={isNursingRobot}
                />
                <div className={healthProductGroupHeaderText}>
                    {isEducator && bridgeLabel && (
                        <p className={healthProductGroupBridgeEyebrow}>
                            {bridgeLabel}
                        </p>
                    )}
                    <h3
                        id={`product-group-${groupId}`}
                        className={`${healthProductGroupTitle} ${
                            isEducator && bridgeLabel ? "mt-1" : ""
                        }`}
                    >
                        {title}
                    </h3>
                    {groupBody && (
                        <p
                            className={`${healthProductGroupBridgeBody} ${
                                isEducator && bridgeLabel ? "" : "mt-2"
                            }`}
                        >
                            {groupBody}
                        </p>
                    )}
                </div>
            </header>

            <div className={listClass}>
                {products.map((product) => (
                    <HealthProductCapabilityRow
                        key={product.id as string}
                        product={product}
                        lang={lang}
                        variant={isDoctor ? "compact" : "default"}
                        muted={isNursingRobot}
                        hideTitle={
                            isNursingRobot &&
                            products.length === 1 &&
                            getLocalized(product?.title, lang) === title
                        }
                    />
                ))}
                {isDoctor && hintLabel && (
                    <ProductGroupCardHint label={hintLabel} variant="doctor-grid" />
                )}
            </div>

            {hintLabel && !isDoctor && (
                <ProductGroupCardHint
                    label={hintLabel}
                    variant="footer"
                    pinned={pinFooterHint}
                />
            )}
        </>
    );

    if (pinFooterHint && hintLabel && !isDoctor) {
        return (
            <div className="flex h-full min-h-0 w-full flex-col">{bodyContent}</div>
        );
    }

    return bodyContent;
}

export function HealthProductGroupCard({
    group,
    lang,
    className = "",
}: HealthProductGroupCardProps) {
    const groupId = group?.id as AimmaProductSuiteGroupId;
    const title = getLocalized(group?.title, lang);
    const products: DataKeys[] = group?.products ?? [];
    const isNursingRobot = groupId === "nursing-robot";
    const isDoctor = groupId === "doctor";
    const isEducator = groupId === "educator";
    const bridgeLabel = getLocalized(group?.bridgeLabel, lang);
    const groupBody = getLocalized(group?.body, lang);

    const groupHref = (group?.href as string | undefined)?.trim();
    const hasGroupLink =
        (groupId === "nurse" ||
            groupId === "doctor" ||
            groupId === "educator" ||
            groupId === "patient") &&
        Boolean(groupHref);
    const hintLabel = hasGroupLink ? getLocalized(group?.ctaLabel, lang) : undefined;

    const bodyProps = {
        groupId,
        title,
        products,
        lang,
        isNursingRobot,
        isDoctor,
        isEducator,
        bridgeLabel: isEducator ? bridgeLabel : undefined,
        groupBody,
        hintLabel,
    };

    if (hasGroupLink && groupHref) {
        const cardClass = isEducator
            ? healthProductGroupCardBridgeLinked
            : healthProductGroupCardLinked;

        return (
            <Link
                to={localeHealthPath(groupHref, lang)}
                className={`${cardClass} ${className}`}
                aria-labelledby={`product-group-${groupId}`}
            >
                <div
                    className={`${healthLinkedCardInnerStable} ${healthProductGroupLinkedInner}`}
                >
                    <ProductGroupCardBody
                        {...bodyProps}
                        pinFooterHint={!isDoctor}
                    />
                </div>
            </Link>
        );
    }

    if (isEducator) {
        return (
            <article
                className={`${healthProductGroupCardBridge} ${className}`}
                aria-labelledby={`product-group-${groupId}`}
            >
                <ProductGroupCardBody {...bodyProps} hintLabel={undefined} />
            </article>
        );
    }

    const cardClass = isNursingRobot
        ? healthProductGroupCardDeveloping
        : healthProductGroupCardStatic;

    return (
        <article
            className={`${cardClass} ${className}`}
            aria-labelledby={`product-group-${groupId}`}
        >
            <ProductGroupCardBody {...bodyProps} hintLabel={undefined} />
        </article>
    );
}
